import { Switch } from "@/components/ui/switch";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Skeleton } from "@/components/ui/skeleton";
import { usd } from "@/components/dash/utils";

const Stat = ({ label, value, sub, cls = "", testid }) => (
  <div className="rounded-lg border border-[#1A2338] bg-[#0F1A2E] p-3">
    <div className="text-[10px] uppercase tracking-widest font-num text-slate-400">{label}</div>
    <div className={`font-num font-bold text-xl mt-1 ${cls}`} data-testid={testid}>{value}</div>
    {sub && <div className="text-[11px] text-slate-500">{sub}</div>}
  </div>
);

const OpenPosition = ({ p, live }) => {
  const q = live?.quotes?.[p.side];
  const value = q ? p.shares * q.bid : null;
  const pnl = value != null ? value - p.cost : null;
  return (
    <div className="mt-4 rounded-lg border border-blue-500/40 bg-blue-500/10 p-3 text-sm" data-testid="open-position">
      <b>Open bet:</b> {p.shares.toFixed(2)} "{p.side}" shares bought at {Math.round(p.entry_price * 100)}¢ for {usd(p.cost)}.
      {value != null && <> Worth <b className="font-num">{usd(value)}</b> now (<span className={pnl >= 0 ? "text-[#34D399]" : "text-[#F87171]"}>{pnl >= 0 ? "+" : ""}{usd(pnl)}</span>).</>}
    </div>
  );
};

export const AccountCard = ({ account: a, live, onToggle, onReset, onBudget }) => {
  if (!a) return <section className="card p-5"><Skeleton className="h-32 bg-[#111A2C]" /></section>;
  const equity = a.balance + a.in_play;
  return (
    <section className="card p-5 sm:p-6 fade-up" data-testid="account-card">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h2 className="font-head font-bold text-lg">Practice account <span className="text-xs font-num text-amber-400 border border-amber-400/40 rounded px-1.5 py-0.5 ml-1">FAKE MONEY</span></h2>
          <p className="text-xs text-slate-400 mt-1">The bot follows the signals automatically: buys {usd(a.rules.bet_size)} bets and cashes out by itself. Runs 24/7 on the server, even when this page is closed.</p>
        </div>
        <div className="flex items-center gap-4">
          <label className="flex items-center gap-2 text-sm">
            <Switch checked={a.enabled} onCheckedChange={onToggle} data-testid="bot-toggle" />
            <span data-testid="bot-status">{a.enabled ? "Auto-trading ON" : "Paused"}</span>
          </label>
          <button onClick={onBudget} className="text-xs text-[#22D3EE] hover:underline" data-testid="change-budget-btn">Change budget</button>
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <button className="text-xs text-slate-500 hover:text-red-400" data-testid="bot-reset-btn">Reset</button>
            </AlertDialogTrigger>
            <AlertDialogContent className="bg-[#0B1220] border-[#1A2338] text-slate-100">
              <AlertDialogHeader>
                <AlertDialogTitle>Reset practice account?</AlertDialogTitle>
                <AlertDialogDescription>This deletes all practice trades and sets the balance back to {usd(a.start_balance)}.</AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel className="bg-transparent border-[#1A2338]" data-testid="bot-reset-cancel">Cancel</AlertDialogCancel>
                <AlertDialogAction onClick={onReset} className="bg-red-500 hover:bg-red-600" data-testid="bot-reset-confirm">Reset</AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-4">
        <Stat label="Cash available" value={usd(a.balance)} sub={a.in_play ? `${usd(a.in_play)} in a bet` : "No open bet"} testid="account-balance" />
        <Stat label="Total profit / loss" value={`${a.total_pnl >= 0 ? "+" : ""}${usd(a.total_pnl)}`} sub={`Started with ${usd(a.start_balance)}`}
          cls={a.total_pnl > 0 ? "text-[#34D399]" : a.total_pnl < 0 ? "text-[#F87171]" : ""} testid="account-pnl" />
        <Stat label="Win rate" value={a.win_rate == null ? "—" : `${a.win_rate}%`} sub={`${a.wins} wins · ${a.losses} losses`} testid="account-winrate" />
        <Stat label="Account value" value={usd(equity)} sub={`${a.closed_count} finished trades`} testid="account-equity" />
      </div>
      <div className="mt-3 text-xs text-slate-400" data-testid="today-pnl">
        Today: <b className={a.today_pnl >= 0 ? "text-[#34D399]" : "text-[#F87171]"}>{a.today_pnl >= 0 ? "+" : ""}{usd(a.today_pnl)}</b>
        {" "}· daily loss limit {usd(a.rules.daily_loss_limit)}{a.today_pnl <= -a.rules.daily_loss_limit ? " — reached, bot paused until tomorrow" : ""}
      </div>
      {a.open_position && <OpenPosition p={a.open_position} live={live} />}
    </section>
  );
};
